import { isNumericOnlySearchQuery } from './typesense-search-config';

type SearchClientLike = {
  search: (requests: any[]) => Promise<{ results: any[] }>;
  clearCache?: () => void;
};

/**
 * Dispatches each InstantSearch request to the main or numeric-only Typesense adapter
 * based on whether `params.query` is digits-only (SKU / barcode style).
 */
export function wrapSearchClientWithNumericRouting(
  mainClient: SearchClientLike,
  numericClient: SearchClientLike,
): SearchClientLike {
  return {
    ...mainClient,
    search: async (requests: any[]) => {
      if (!requests?.length) {
        return mainClient.search(requests);
      }

      const mainIndices: number[] = [];
      const numericIndices: number[] = [];

      requests.forEach((req, i) => {
        const q = req.params?.query?.trim() ?? '';
        if (isNumericOnlySearchQuery(q)) {
          numericIndices.push(i);
        } else {
          mainIndices.push(i);
        }
      });

      // Important: if we are not actually routing, return the exact adapter
      // response for the exact request array. Any reordering can break
      // InstantSearch's expectation about result<->request alignment.
      if (numericIndices.length === 0) {
        return mainClient.search(requests);
      }
      if (mainIndices.length === 0) {
        return numericClient.search(requests);
      }

      const results: any[] = new Array(requests.length);

      if (mainIndices.length) {
        const r = await mainClient.search(
          mainIndices.map((i) => requests[i]),
        );
        mainIndices.forEach((orig, j) => {
          results[orig] = r.results[j];
        });
      }

      if (numericIndices.length) {
        const r = await numericClient.search(
          numericIndices.map((i) => requests[i]),
        );
        numericIndices.forEach((orig, j) => {
          results[orig] = r.results[j];
        });
      }

      return { results };
    },
  };
}
